import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ActionButton, ButtonPrimary } from "ui/base/Button/ButtonStyled";
import { defaultThemeColors, fonts, mediaSizes, themeColor } from "ui/theme";
import { Modal } from "ui/base/modal/Modal";
import { getAvatarUrl } from "utils";
import { DateCard } from "ui/base/dateCard/DateCard";
import { DatePicker } from "ui/base/datePicker/DatePicker";
import moment from "moment";
import { useTeacherSlots } from "hooks/useTeacherSlots";
import { getTeacherDetailsById } from "api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { defaultTypeClasses } from "conts";
import { UserIcon } from "ui/icons";
import { rescheduleBooking } from "api/booking.api";
import { BlueTicket } from "ui/icons/BlueTicket";
import { useToast } from "hooks";
import { useFilters } from "../../../../context/FilterContext";
import { useNavigate } from "react-router-dom";
import { userRoute } from "pages/user/user.routes";
import { CheckMarkIcon } from "../../../../ui/icons/CheckMark";

export const RescheduleModal = ({
    show,
    teacherUserId,
    closeModal,
    classType,
    students,
    teacherFirstName,
    nameOfClass,
    teacherPic,
    bookingSlot_id,
    booking_id,
    booking_date,
    booking_time,
    isOnline
}) => {
    const { filters } = useFilters();
    const [classDate, setClassDate] = useState(null);
    const [showThankYou, setShowThankYou] = useState(false);
    const queryClient = useQueryClient();
    const navigate = useNavigate()

    const handleNavigation = () => {
        if (!teacherUserId) return;

        navigate(`${userRoute.classDetail}${teacherUserId}`)
    }

    const { data } = useQuery({
        queryKey: ["getTeacherDetailsById", teacherUserId],
        queryFn: () => getTeacherDetailsById({ id: teacherUserId }),
        enabled: !!teacherUserId,
    });

    const {
        selectedDate,
        setSelectedDate,
        typeOfClass,
        setTypeOfClass,
        availableTimings,
        slotsFilterDate,
        handleMonthChange,
    } = useTeacherSlots({
        teacherInfo: data?.data?.teacher,
        online_type: isOnline ? "online" : 'offline',
        booking_date,
        booking_time
    });

    const { mutate } = useMutation({
        mutationFn: rescheduleBooking,
        onError: () => useToast("error", "Re-schedule failed"),
        onSuccess: () => {
            useToast("success", "Your class has been Re-scheduled");
            queryClient.invalidateQueries(["getFutureClassesForUser2"]);
            setShowThankYou(true);
        },
    });

    useEffect(() => {
        if (!data?.data?.teacher || typeOfClass.value.toLowerCase() === classType) return;

        setTypeOfClass(defaultTypeClasses.Group);
    }, [classType, typeOfClass.value]);

    const handleSubmit = () => {
        mutate({
            bookingSlot_id,
            booking_id,
            new_class_date: moment(classDate.date).format("YYYY-MM-DD"),
            new_class_time: moment(classDate.time, ["h:mm A"]).format("HH:mm:ss"),
        });
    };

    const badgeVariant = isOnline
        ? "black_fade_small"
        : classType === "private"
            ? "primary_fade_small"
            : "destructive_fade_small"

    return (
        <Modal
            show={show}
            handleClose={closeModal}
            maxWidth={showThankYou ? 510 : 860}
            minHeight={showThankYou ? undefined : 420}
            // contentWrapperHeight={showThankYou ? undefined : "420px"}
            setContentHeightFull
        >
            {!showThankYou ? (
                <ModalBody>
                    <div>
                        <ModalHeader>
                            <ModalTitle>Re-schedule request</ModalTitle>
                            <ModalSubTitle>Choose a new time for your class</ModalSubTitle>
                        </ModalHeader>
                        <LeftContent>
                            <DatePicker
                                minDate={moment().toDate()}
                                date={selectedDate}
                                setDate={setSelectedDate}
                                filterDate={slotsFilterDate}
                                dayClassName={(date) => {
                                    return slotsFilterDate(date) ? "" : "unavailable-date";
                                }}
                                labelSize={"small"}
                                onMonthChange={handleMonthChange}
                            />
                            <StyledSpan>Upcoming Classes</StyledSpan>
                            <ScheduleCardContainer>
                                {availableTimings?.map((timing) => {
                                    const isDisabled =
                                        classDate &&
                                        (classDate.date !== timing.date ||
                                            classDate.time !== timing.time);
                                    return (
                                        <DateCard
                                            key={timing.id}
                                            date={timing}
                                            action={
                                                <ActionButton
                                                    disabled={isDisabled}
                                                    variant={classDate && !isDisabled ? "primary" : "outline"}
                                                    onClick={() =>
                                                        classDate && !isDisabled
                                                            ? setClassDate(null)
                                                            : setClassDate(timing)
                                                    }
                                                >
                                                    {classDate && !isDisabled ? <CheckMarkIcon /> : "Choose"}
                                                </ActionButton>
                                            }
                                        />
                                    );
                                })}
                            </ScheduleCardContainer>
                        </LeftContent>
                    </div>
                    <RightPart>
                        <ClassInfo>
                            <ClassInfoHeader>
                                <Avatar onClick={handleNavigation}>
                                    {teacherPic ? (
                                        <img
                                            src={getAvatarUrl(teacherPic)}
                                            alt={teacherFirstName}
                                        />
                                    ) : (
                                        <UserIcon color="txt" />
                                    )}
                                </Avatar>

                                <Info>
                                    <NameIconTextWrapper onClick={handleNavigation}>
                                        <Name>{teacherFirstName}</Name>
                                    </NameIconTextWrapper>
                                    <Details>
                                        <span>
                                            <UserIcon color="txt" width="15" height="15" />
                                        </span>
                                        <span>{students}</span> | <span>{nameOfClass}</span>
                                    </Details>
                                    <ButtonPrimary
                                        variant={badgeVariant}
                                        style={{
                                            pointerEvents: "none",
                                            width: "3rem",
                                            fontSize: "12px",
                                            padding: "10px 17px",
                                        }}
                                    >
                                        {isOnline ? "Online" : classType}
                                    </ButtonPrimary>
                                </Info>
                            </ClassInfoHeader>
                            <NewDateSection>
                                <span>New Date</span>
                                <span>New Time</span>
                                <NewDateText>{classDate?.date}</NewDateText>
                                <NewDateText>{classDate?.time}</NewDateText>
                            </NewDateSection>
                            <ClassInfoAction>
                                <ButtonPrimary disabled={!classDate} onClick={handleSubmit}>
                                    Re-schedule Class
                                </ButtonPrimary>
                                <ButtonPrimary variant="outline" onClick={closeModal}>
                                    Close
                                </ButtonPrimary>
                            </ClassInfoAction>
                        </ClassInfo>
                    </RightPart>
                </ModalBody>
            ) : (
                <ThankYouContent>
                    <BlueTicket />
                    <ThankYouTitle>Re-schedule request has been sent!</ThankYouTitle>
                    <ThankYouMessage>
                        The instructor must approve the reschedule request for it to be confirmed. Otherwise, the original schedule will remain.
                    </ThankYouMessage>
                    <ButtonPrimary style={{
                        width: "80%",
                    }} onClick={closeModal}>Done</ButtonPrimary>
                </ThankYouContent>
            )}
        </Modal>
    );
};

const ModalBody = styled.div`
    display: grid;
    gap: 1.5rem;
    height: 100%;

    @media (width >= ${mediaSizes.md}) {
        grid-template-columns: 1fr 1fr;
        padding-block: 0.5rem;
        padding-inline: 1rem;
    }
    @media (width >= ${mediaSizes.lg}) {
        padding-inline: 1.5rem;
    }
`;

const LeftContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const RightPart = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    height: fit-content;
`;

const ModalHeader = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
    padding-bottom: 20px;
`;

const StyledSpan = styled.span`
    font-weight: 600;
    font-size: 18px;
`;

const ModalTitle = styled.h2`
    font-weight: 600;
    font-size: 22px;
    line-height: 100%;
    @media (width >= ${mediaSizes.md}) {
        font-size: 26px;
    }
`;

const ModalSubTitle = styled.span`
    font-weight: 400;
    font-size: 18px;
    line-height: 155%;
`;

const ScheduleCardContainer = styled.div`
    max-height: 320px;
    overflow-y: auto;
    display: grid;
    gap: 1.2rem;
`;

const ClassInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    padding: 0;
    box-shadow: 0px 13px 18px 0px #18155a0d;
    order: 2;
    padding-block: 1rem;
    @media (width >= ${mediaSizes.md}) {
        width: 410px;
        padding: 1rem;
    }
    @media (width >= ${mediaSizes.lg}) {
        padding: 30px;
    }
`;

const ClassInfoHeader = styled.div`
    display: flex;
    gap: 20px;
`;

const ClassInfoAction = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const NewDateSection = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: fit-content;
    column-gap: 2rem;
    row-gap: 1rem;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 1px;
    text-transform: uppercase;
    text-align: center;
    color: ${themeColor("placeholder")};
`;

const NewDateText = styled.p`
    text-transform: none;
    letter-spacing: 0px;
    font-weight: 400;
    font-size: 14px;
    text-align: center;
    color: ${themeColor("black")};
`

const Avatar = styled.div`
    width: 100px;
    height: 100px;
    background-color: ${defaultThemeColors.inputBgNeutral};
    border-radius: 8px;
    object-fit: cover;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    & > svg {
        height: 34px;
        width: 34px;
        opacity: 0.5;
    }

    & > img {
        height: 100%;
        width: 100%;
        object-fit: cover;
    }
`;

const Info = styled.div`
    flex: 1;
    display: grid;
    gap: 10px;
`;

const Name = styled.div`
    font-size: ${fonts.sizes.regular};
    font-weight: bold;
`;

const Details = styled.div`
    font-size: ${fonts.sizes.small};
    color: ${defaultThemeColors.txt};
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.3rem;
    opacity: 0.4;
`;

const NameIconTextWrapper = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
    width: fit-content;
`;

const ThankYouContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
`;

const ThankYouTitle = styled.h2`
    font-weight: 600;
    font-size: 18px;
    letter-spacing: 0%;
    text-align: center;
`;

const ThankYouMessage = styled.p`
    font-weight: 400;
    font-size: 14px;
    line-height: 155%;
    letter-spacing: 0%;
    text-align: center;
`;
